import styled from 'styled-components'
import { useNavigate } from 'react-router'
import { useMemo, useState } from 'react'
import { fetcher } from '../utils/fetcher'
import { Title } from 'react-head'
import { Button } from '@bm-js/atlascloud-ui'
import Loader from '../components/Loader'
import { breakPointsCss, colors } from '../utils/config'
import DeleteConfirmation from '../components/DeleteConfirmation'
import CourseForm, { CourseInitial } from '../components/CourseForm'
import CourseCard from '../components/CourseCard'
import { CourseBM } from '@bm-js/atlascloud-shared'
import { usePopup, useToast, useToken } from '../utils/hooks'
import { useApi } from '../utils/useApi'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

const StyledCourses = styled.div`
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  ${breakPointsCss.tablet} {
    .headers {
      display: block;
    }
  }
`

const Courses = () => {
  //Global
  const navigate = useNavigate()
  const toast = useToast()
  const token = useToken()
  const { setPopup, closePopup } = usePopup()
  const { tr } = useTr()

  const { data: courses = [], refetch } = useApi<CourseBM[]>('courses')

  const [loading, setLoading] = useState(false)

  const deleteCourseInit = (course: CourseBM) => {
    setPopup(
      <DeleteConfirmation
        item={course}
        deleteKey={'name'}
        name={tr(TrKey.theCourse)}
        handleCancel={closePopup}
        handleDelete={deleteCourse}
      />
    )
  }

  const deleteCourse = async (course: CourseBM, cb: () => void) => {
    setLoading(true)
    const { err } = await fetcher({
      path: `courses/${course._id}`,
      method: 'DELETE',
      auth: token,
    })
    if (err) {
      cb()
      toast.error(tr(TrKey.coursesFailedToDelete))
      setLoading(false)
      return
    }
    cb()
    closePopup()
    toast.success(tr(TrKey.coursesCourseDeleted))
    await refetch()
    setLoading(false)
  }

  const createCourseInit = () => {
    setPopup(<CourseForm handleSubmit={createCourse} closePopup={closePopup} />)
  }

  const createCourse = async (
    formData: CourseInitial,
    cb: () => void,
    errCb: () => void
  ) => {
    const { err, data: courseId } = await fetcher({
      path: 'courses',
      method: 'POST',
      body: formData,
      auth: token,
    })
    if (err) {
      toast.error(tr(TrKey.coursesFailedToCreate))
      errCb()
      return
    }
    toast.success(tr(TrKey.coursesCourseCreated))
    cb()
    navigate(`/courses/${courseId}`)
    closePopup()
  }

  const title = useMemo(() => {
    return tr(TrKey.coursesRouteTitle) + ' | Atlascloud'
  }, [tr])

  if (loading)
    return (
      <StyledCourses>
        <div className='loader-container'>
          <Loader color={colors.primary400} />
        </div>
      </StyledCourses>
    )
  return (
    <StyledCourses className='content'>
      <Title>{title}</Title>
      <div className='headers jcsb ac mbottom1 flex'>
        <h1 className='m0 l primblue'>{tr(TrKey.coursesRouteTitle)}</h1>
        <Button onClick={createCourseInit}>{tr(TrKey.createCourse)}</Button>
      </div>
      <div>
        {courses.map((course) => (
          <CourseCard
            deleteCourse={() => deleteCourseInit(course)}
            course={course}
            key={course._id}
          />
        ))}
      </div>
    </StyledCourses>
  )
}

export default Courses
